import { useEffect, useState } from "react";
import { Loader2, Plus, UserX } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { ROLES, useAuth } from "@/lib/auth";
import DataTable, { EmptyState } from "@/components/app/DataTable";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Members() {
  const { user, orgs } = useAuth();
  const activeOrg = orgs.find((o) => o.id === user?.active_org_id);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "operator", password: "" });
  const canEdit = user?.platform_role === "super_admin" || user?.active_role === "admin_mitra";

  const load = async () => {
    if (!activeOrg) return;
    setLoading(true);
    try { const { data } = await api.get(`/organizations/${activeOrg.id}/members`); setMembers(data); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [activeOrg?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/organizations/${activeOrg.id}/invitations`, form);
      toast.success(`Undangan dikirim ke ${form.email}`);
      setOpen(false); setForm({ email: "", name: "", role: "operator", password: "" }); load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const updateRole = async (m, role) => {
    try { await api.patch(`/organizations/${activeOrg.id}/members/${m.id}`, { role }); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Hapus anggota "${m.name || m.email}" dari ${activeOrg?.name}?`)) return;
    try {
      await api.delete(`/organizations/${activeOrg.id}/members/${m.id}`);
      toast.success("Anggota dihapus.");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="gc-label">Administrasi</span>
          <h1 className="gc-h1 mt-1">Anggota Perusahaan Pengguna</h1>
          <p className="mt-2 text-slate-500">Kelola pengguna dan peran di <strong>{activeOrg?.name}</strong>. Manajemen ini dilakukan oleh admin perusahaan pengguna, sedangkan sistem GREENCON HPP dimiliki dan dikelola oleh Politeknik Negeri Manado.</p>
        </div>
        {canEdit && <button onClick={() => setOpen(true)} className="gc-btn-primary" data-testid="btn-invite"><Plus className="w-4 h-4" /> Undang Anggota</button>}
      </div>

      {loading ? <div className="gc-card"><Loader2 className="w-4 h-4 animate-spin" /></div> : (
        <DataTable
          rows={members}
          columns={[
            { key: "name", header: "Nama" },
            { key: "email", header: "Email" },
            { key: "role", header: "Peran", render: (r) => canEdit ? (
              <select value={r.role} onChange={(e) => updateRole(r, e.target.value)} className="gc-input h-9 max-w-[220px]" data-testid={`role-${r.email}`}>
                {Object.entries(ROLES).filter(([k]) => k !== "super_admin").map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            ) : ROLES[r.role] || r.role },
            { key: "is_active", header: "Aktif", render: (r) => r.is_active ? <span className="gc-badge bg-emerald-50 text-emerald-700 border border-emerald-200">Aktif</span> : <span className="gc-badge bg-slate-100">Nonaktif</span> },
            { key: "actions", header: "Aksi", align: "right", render: (r) => canEdit ? (
              <button onClick={() => removeMember(r)}
                      className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-semibold text-rose-700 hover:bg-rose-50 border border-rose-200"
                      data-testid={`btn-remove-${r.email}`}>
                <UserX className="w-3.5 h-3.5" /> Hapus
              </button>
            ) : null },
          ]}
          empty={<EmptyState title="Belum ada anggota" desc="Undang tim untuk berkolaborasi." />}
        />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-cabinet">Undang Anggota Baru</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div><label className="gc-label block mb-1.5">Nama</label><input required className="gc-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inv-name" /></div>
            <div><label className="gc-label block mb-1.5">Email</label><input required type="email" className="gc-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="inv-email" /></div>
            <div><label className="gc-label block mb-1.5">Peran</label>
              <select className="gc-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="inv-role">
                {Object.entries(ROLES).filter(([k]) => k !== "super_admin").map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div><label className="gc-label block mb-1.5">Kata Sandi Awal</label><input required type="text" className="gc-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="minimal 6 karakter" data-testid="inv-password" /></div>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <button type="button" onClick={() => setOpen(false)} className="gc-btn-ghost">Batal</button>
              <button type="submit" className="gc-btn-primary" data-testid="inv-submit">Undang</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
